import { type JSX, useEffect, useId, useState } from 'react';

import {
  PRODUKTARTNAME,
  BEZUGSNAME,
  VERTRIEBSFORMNAME,
  type Mengeneinheit,
  type Produktart,
  type Steuerschluessel,
  type Vertriebsform,
  type Zusatzleistungsbezug,
  type Warengruppe,
  anlegen as apiAnlegen,
  steuerschluessel as holeSteuerschluessel,
  warengruppen as holeWarengruppen,
} from '../api/produkte';
import { ApiFehler, type Verstoss } from '../api/fehler';
import { Feld } from '../komponenten/Feld';
import { Abschnitt, Feldraster } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Meldung } from '../komponenten/Meldung';
import { KUNDENARTEN, KUNDENARTNAME, type Kundenart } from '../api/kunden';

const EINHEITEN: readonly Mengeneinheit[] = [
  'Stk',
  'Platz',
  'Termin',
  'Std',
  'Tag',
  'Block',
  'Person',
  'Pauschal',
];

const FORMEN: readonly Vertriebsform[] = [
  'einzelplatz',
  'exklusivtermin',
  'baustein',
  'ressource',
  // AA-009, genehmigt am 31.08.2026: alles, was zu einer Buchung
  // dazugekauft wird — Verpflegung, Leihausruestung, Fahrzeugmiete,
  // Begleitperson (BUC-83).
  'zusatzleistung',
];

const BEZUEGE: readonly Zusatzleistungsbezug[] = ['teilnehmer', 'buchung'];

/** Die Felder, die ihre Verstoesse selbst zeigen (ERR-03). */
const AM_FELD = [
  'bezeichnung',
  'mindestdauerBloecke',
  'vertriebsformen',
  'einkaufspreis',
  'bezugZusatzleistung',
] as const;

/**
 * Die Vorbelegung je Produktart (PRD-28).
 *
 * "Die Art bestimmt Pflichtfelder, Planungsregeln und Belegverhalten" — und
 * damit auch, was sinnvoll voreingestellt ist. Ein Fahrtraining wird nach
 * Plaetzen verkauft, eine Ressource nach Stunden, Verpflegung nach Personen.
 * Eine Vorbelegung, die der Anwender unveraendert uebernimmt, zaehlt nach
 * NFA-21 nicht als Bedienschritt.
 */
const VORBELEGUNG: Record<
  Produktart,
  { readonly einheit: Mengeneinheit; readonly formen: readonly Vertriebsform[] }
> = {
  fahrtraining: { einheit: 'Platz', formen: ['einzelplatz', 'exklusivtermin'] },
  event: { einheit: 'Termin', formen: ['exklusivtermin'] },
  verpflegung: { einheit: 'Person', formen: ['einzelplatz'] },
  ware: { einheit: 'Stk', formen: ['einzelplatz'] },
  dienstleistung: { einheit: 'Std', formen: ['einzelplatz'] },
  baustein: { einheit: 'Block', formen: ['baustein'] },
  ressource: { einheit: 'Std', formen: ['ressource'] },
  /* C18 (0274): Der Gutschein wird angelegt, dann sein Betrag gepflegt, dann buchbar gestellt. */
  gutschein: { einheit: 'Stk', formen: ['einzelplatz'] },
};

/**
 * Artikel anlegen.
 *
 * Seitentyp Erfassung (ÄA-41): eine Karte mit aufklappbaren Abschnitten,
 * ohne Reiter und ohne Strecke, mit der PRODUKTART als erstem Umschalter — sie ist die einzige Angabe, die das Formular umbaut, und sie
 * entscheidet nach PRD-35 auch den Nummernkreis. Wer sie spaeter wechseln
 * will, legt einen neuen Artikel an: Die Nummer ist dann eine andere, und
 * eine Nummer aus dem falschen Kreis liesse sich nicht mehr berichtigen.
 *
 * DIE ARTIKELNUMMER IST KEIN EINGABEFELD. Sie entsteht beim Speichern aus dem
 * Kreis der Produktart (DAT-18) und steht vorher als das da, was sie ist —
 * nicht als leeres Kaestchen, das jemand ausfuellen moechte.
 */
export function ArtikelAnlegen({
  anfangsBezeichnung = '',
  beiAngelegt,
  beiAbbruch,
}: {
  readonly anfangsBezeichnung?: string;
  readonly beiAngelegt: (id: string, artikelnummer: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  const wahlId = useId();
  const [produktart, setzeProduktart] = useState<Produktart>('fahrtraining');
  const [bezeichnung, setzeBezeichnung] = useState(anfangsBezeichnung);
  const [kurztext, setzeKurztext] = useState('');
  const [einheit, setzeEinheit] = useState<Mengeneinheit>('Platz');
  const [warengruppe, setzeWarengruppe] = useState('');
  const [steuer, setzeSteuer] = useState('');
  const [formen, setzeFormen] = useState<readonly Vertriebsform[]>(['einzelplatz', 'exklusivtermin']);
  // BUC-105: alle drei vorbelegt — einschraenken ist die bewusste Handlung.
  const [arten, setzeArten] = useState<readonly Kundenart[]>([...KUNDENARTEN]);
  /*
   * BUC-84, AA-009. Vorbelegt mit "je Teilnehmer": Von den vier
   * Zusatzleistungen aus BUC-83 gelten drei je Person — Verpflegung,
   * Leihausruestung, Begleitperson —, und nur die Fahrzeugmiete je Buchung.
   */
  const [bezug, setzeBezug] = useState<Zusatzleistungsbezug>('teilnehmer');
  const [bloecke, setzeBloecke] = useState('');
  const [einkaufspreis, setzeEinkaufspreis] = useState('');
  const [gruppen, setzeGruppen] = useState<readonly Warengruppe[]>([]);
  const [schluessel, setzeSchluessel] = useState<readonly Steuerschluessel[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);

  useEffect(() => {
    void holeWarengruppen().then(setzeGruppen).catch(() => setzeGruppen([]));
    void holeSteuerschluessel().then(setzeSchluessel).catch(() => setzeSchluessel([]));
  }, []);

  /** Der Wechsel der Produktart belegt Einheit und Vertriebsformen neu (PRD-28). */
  function wechsleArt(neu: Produktart): void {
    setzeProduktart(neu);
    setzeEinheit(VORBELEGUNG[neu].einheit);
    setzeFormen(VORBELEGUNG[neu].formen);
  }

  const verstoesseZu = (feld: string): readonly Verstoss[] => fehler?.alleZumFeld(feld) ?? [];

  const gewaehlteGruppe = gruppen.find((g) => g.id === warengruppe);

  function absenden(): void {
    setzeLaeuft(true);
    setzeFehler(undefined);
    void apiAnlegen({
      bezeichnung,
      produktart,
      mengeneinheit: einheit,
      kurztext: kurztext.trim() === '' ? null : kurztext.trim(),
      langtext: null,
      warengruppe: warengruppe === '' ? null : warengruppe,
      steuerschluessel: steuer === '' ? null : steuer,
      vertriebsformen: formen,
      angebotenAn: arten,
      bezugZusatzleistung: formen.includes('zusatzleistung') ? bezug : null,
      tarif: 'standard',
      mindestdauerBloecke: bloecke === '' ? null : Number(bloecke),
      einkaufspreis: einkaufspreis.trim() === '' ? null : einkaufspreis.trim(),
    })
      .then((neu) => beiAngelegt(neu.id, neu.artikelnummer))
      .catch((u: unknown) => setzeFehler(u as ApiFehler))
      .finally(() => setzeLaeuft(false));
  }

  /*
   * ÄA-41, Seitentyp Erfassung: „Neuer Artikel" steht im Seitenkopf, die
   * Karte trägt vier aufklappbare Abschnitte. „Speichern" steht oben rechts
   * und am Ende — es bleibt EIN Formular mit EINER Prüfung.
   */
  return (
    <div className="maske maske--weit erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} gezeigteFelder={AM_FELD} />}

      <Erfassung
        beschriftung="Neuer Artikel"
        beiAbsenden={absenden}
        absenden={{ text: laeuft ? 'Wird gespeichert …' : 'Speichern', gesperrt: laeuft }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        {/*
          Die Produktart steht ganz oben, weil sie als einzige Angabe das
          Formular UMBAUT — und weil sie den Nummernkreis entscheidet (PRD-35).
          Sie ist die einzige Angabe dieses Formulars, die sich spaeter nicht
          mehr berichtigen laesst.
        */}
        <Abschnitt titel="Produktart" zusatz="entscheidet den Nummernkreis">
          <fieldset className="wahlband">
            <legend className="nur-vorlesen">Produktart</legend>
            <div className="wahlband__reihe">
              {(Object.keys(PRODUKTARTNAME) as Produktart[]).map((art) => {
                const id = `${wahlId}-${art}`;
                return (
                  <div key={art} className="wahlband__wahl">
                    <input
                      id={id}
                      type="radio"
                      name="produktart"
                      value={art}
                      checked={produktart === art}
                      onChange={() => wechsleArt(art)}
                    />
                    <label className="wahlband__name" htmlFor={id}>
                      {PRODUKTARTNAME[art]}
                    </label>
                  </div>
                );
              })}
            </div>
          </fieldset>
        </Abschnitt>

        <Abschnitt titel="Was verkauft wird" zusatz="* = Pflichtangabe">
          <Feldraster>
            <Feld
              beschriftung="Bezeichnung"
              wert={bezeichnung}
              aendern={setzeBezeichnung}
              pflicht
              verstoesse={verstoesseZu('bezeichnung')}
            />
            <Feld
              beschriftung="Kurztext"
              wert={kurztext}
              aendern={setzeKurztext}
              hinweisText="erscheint auf dem Beleg"
            />

            {/* PRD-35, DAT-18: kein leeres Kaestchen, das jemand ausfuellen
                moechte — die Nummer entsteht beim Speichern aus dem Kreis
                der Produktart. */}
            <div className="feld feld--gesperrt feld--reserviert">
              <span className="feld__beschriftung">Artikelnummer</span>
              <p className="feld__ersatz">wird beim Speichern vergeben</p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor={`${wahlId}-einheit`}>
                Mengeneinheit
              </label>
              <select
                id={`${wahlId}-einheit`}
                value={einheit}
                onChange={(e) => setzeEinheit(e.target.value as Mengeneinheit)}
              >
                {EINHEITEN.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>

            {/* PRD-44 */}
            {produktart === 'ressource' && (
              <Feld
                beschriftung="Mindestbuchungsdauer in Blöcken"
                wert={bloecke}
                aendern={setzeBloecke}
                pflicht
                eingabeart="numeric"
                hilfe="Ohne sie wäre die Ressource nach Minuten abrechenbar."
                verstoesse={verstoesseZu('mindestdauerBloecke')}
              />
            )}

            {/* PRD-18 */}
            <Feld
              beschriftung="Einkaufspreis"
              wert={einkaufspreis}
              aendern={setzeEinkaufspreis}
              hilfe="Nur für die Deckungsbeitragsanzeige. Wer ihn nicht sehen darf, kann ihn auch nicht setzen."
              verstoesse={verstoesseZu('einkaufspreis')}
            />
          </Feldraster>
        </Abschnitt>

        <Abschnitt titel="Einordnung" zusatz="Warengruppe und Steuer">
          <Feldraster>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor={`${wahlId}-gruppe`}>
                Warengruppe
              </label>
              <select
                id={`${wahlId}-gruppe`}
                value={warengruppe}
                onChange={(e) => setzeWarengruppe(e.target.value)}
              >
                <option value="">— keine —</option>
                {gruppen.map((g) => (
                  <option key={g.id} value={g.id}>
                    {'\u00a0'.repeat(g.tiefe * 2)}
                    {g.bezeichnung}
                  </option>
                ))}
              </select>
              {gewaehlteGruppe?.steuerschluessel !== undefined
                && gewaehlteGruppe.steuerschluessel !== null && (
                  <p className="feld__hilfe">
                    Diese Gruppe gibt den Steuerschlüssel {gewaehlteGruppe.steuerschluessel} vor.
                    Lassen Sie das Feld daneben leer, um ihn zu erben.
                  </p>
                )}
            </div>

            {/*
              PRD-02: Leer heisst „von der Warengruppe" und nicht „keiner".
              Der Unterschied ist der Grund fuer den Hinweis daneben — ohne
              ihn traegt der Anwender einen Wert ein, den er schon hat.
            */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor={`${wahlId}-steuer`}>
                Steuerschlüssel
              </label>
              <select
                id={`${wahlId}-steuer`}
                value={steuer}
                onChange={(e) => setzeSteuer(e.target.value)}
              >
                <option value="">— von der Warengruppe —</option>
                {schluessel.map((s) => (
                  <option key={s.schluessel} value={s.schluessel}>
                    {s.schluessel} · {s.bezeichnung} ({s.prozent} %)
                  </option>
                ))}
              </select>
            </div>
          </Feldraster>
        </Abschnitt>

        <Abschnitt titel="Vertrieb" zusatz="Vertriebsformen und Kundenarten">
          <Feldraster>
            {/* PRD-42: Ein Produkt kann mehrere Formen zugleich tragen,
                jede mit eigener Preisstellung. Deshalb Kaestchen und keine
                Auswahl. */}
            <fieldset className="unterblock">
              <legend>Wie darf der Artikel verkauft werden?</legend>
              {FORMEN.map((f) => (
                <label key={f} className="wahlreihe">
                  <input
                    type="checkbox"
                    checked={formen.includes(f)}
                    onChange={(e) =>
                      setzeFormen((alt) =>
                        e.target.checked ? [...alt, f] : alt.filter((x) => x !== f),
                      )
                    }
                  />
                  {VERTRIEBSFORMNAME[f]}
                </label>
              ))}
              {verstoesseZu('vertriebsformen').map((v) => (
                <p key={v.kennung} className="feld__fehler-satz">
                  {v.meldung.wasGeschah} {v.meldung.naechsterSchritt}
                </p>
              ))}
            </fieldset>

            {/*
              BUC-105: "Die Kundenart bestimmt, welche Produkte ...
              ueberhaupt angeboten werden." Alle drei sind angehakt —
              einschraenken ist die bewusste Handlung, nicht der
              Ausgangszustand. Wer nichts anhakt, kommt nicht weiter: ein
              Artikel, den niemand buchen darf, wird gesperrt und nicht
              leergeraeumt.
            */}
            <fieldset className="unterblock">
              <legend>Wem wird der Artikel angeboten?</legend>
              {KUNDENARTEN.map((a) => (
                <label key={a} className="wahlreihe">
                  <input
                    type="checkbox"
                    checked={arten.includes(a)}
                    onChange={(e) =>
                      setzeArten((alt) =>
                        e.target.checked ? [...alt, a] : alt.filter((x) => x !== a),
                      )
                    }
                  />
                  {KUNDENARTNAME[a]}
                </label>
              ))}
              {/* PRD-03 */}
              {arten.length === 0 && (
                <p className="feld__fehler">
                  Mindestens eine Kundenart. Soll der Artikel niemandem offenstehen, setzen
                  Sie ihn nach der Anlage auf „gesperrt".
                </p>
              )}
            </fieldset>

            {/*
              BUC-84: "ob sie je Buchung oder je Teilnehmer gelten". Das
              IST die Preisstellung der Zusatzleistung (PRD-42) — Kaffee je
              Teilnehmer, Kaffeekanne je Buchung. Das Feld erscheint nur
              mit der Form, weil es ohne sie nichts aussagt.
            */}
            {formen.includes('zusatzleistung') && (
              <fieldset className="unterblock">
                <legend>Worauf bezieht sich der Preis der Zusatzleistung?</legend>
                {BEZUEGE.map((b) => (
                  <label key={b} className="wahlreihe">
                    <input
                      type="radio"
                      name="bezugZusatzleistung"
                      checked={bezug === b}
                      onChange={() => setzeBezug(b)}
                    />
                    {BEZUGSNAME[b]}
                  </label>
                ))}
                <p className="feld__hilfe">
                  Eine Tasse Kaffee gilt je Teilnehmer, eine Kanne für den Tisch je Buchung.
                </p>
              </fieldset>
            )}
          </Feldraster>
        </Abschnitt>
      </Erfassung>
    </div>
  );
}
